import { MoneyV2 } from "@ts/shopify-storefront";
import { useCallback } from "react";
import { useLocalisationContext } from "./useLocalisation";
import { useSale } from "./useSale";

import { useSite } from "~/hooks/useSite";

export const useShopify = () => {
  const site = useSite();

  const { currentLocale } = useLocalisationContext();
  const { sale, isSaleActive } = useSale();
  const isOnSale = isSaleActive();

  const imageUrl = (src: string, size = -1): string => {
    const dimensions = `${size}x${size}`;
    const match = src?.match(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i,
    );

    return match &&
      src?.includes("shopify.com") &&
      !src?.includes("res.cloudinary.com/baredfootwear/image/fetch/f_auto") &&
      size !== -1
      ? `https://res.cloudinary.com/baredfootwear/image/fetch/f_auto/${src?.split(match[0])[0]}_${dimensions}${match[0]}`.replace(
          /http(s)?:/,
          ``,
        )
      : src;
  };

  const imageSrcSets = (src, size) =>
    src?.includes(`shopify.com`)
      ? [1, 150, 200, 400, 500, 768, 1000, 1500, 2000]
          .filter((set) => !size || (size && size >= set))
          .map((set) => `${imageUrl(src, set.toString())} ${set}w`)
          .join(`,`)
      : "";

  const decodeVariantId = (id?: string) => {
    return id ? id.split(`ProductVariant/`)[1] : id;
  };

  const getBadgeFromTags = useCallback(
    (
      tags?: string[],
      productStatus?: string,
      availableForSale?: boolean,
      launchDateMetafield?: string,
      prices?: {
        price: MoneyV2;
        compareAtPrice: MoneyV2 | null;
      },
    ): Badge | undefined => {
      //Resolve notify me/out of stock badge based off inventory count and product status (Convert this to use availableForSale when possible)
      if (!availableForSale && productStatus) {
        return ["Seasonal", "Core"].includes(productStatus)
          ? { name: "Notify Me", variant: "secondary" }
          : { name: "Out Of Stock", variant: "warning" };
      }

      // If product has a discount, show the percentage off
      const compareAmount = Number(prices?.compareAtPrice?.amount ?? 0);
      const currentAmount = Number(prices?.price?.amount ?? 0);

      if (
        compareAmount > 0 &&
        currentAmount > 0 &&
        compareAmount > currentAmount
      ) {
        const exactDiscountPercentage =
          ((compareAmount - currentAmount) / compareAmount) * 100;
        // Add sensitivity buffer - anything within 0.05% of next tier gets bumped up
        const sensitivityBuffer = 0.05;
        const adjustedDiscountPercentage =
          exactDiscountPercentage + sensitivityBuffer;

        const roundedDiscount = Math.floor(adjustedDiscountPercentage / 5) * 5; // Round down to nearest 5
        return { name: `${roundedDiscount}% Off`, variant: "secondary" };
      }

      // If product is on preorder, set Preorder.
      if (tags?.includes("Preorder")) {
        return { name: "Pre-Order Now", variant: "light" };
      }

      // Check if we're in a country with retail stores (NZ) and product is tagged as OnlineOnly for that country
      if (
        ["NZ"].includes(site.countryCode) &&
        tags?.includes(`${site.countryCode}:OnlineOnly`)
      ) {
        return { name: "Online Only", variant: "light" };
      }

      // If the product is within last 4 weeks of launch date.
      if (launchDateMetafield) {
        const launchDate = new Date(launchDateMetafield);
        const now = new Date();
        const fourWeeksAgo = new Date();
        fourWeeksAgo.setDate(now.getDate() - 28);
        if (launchDate >= fourWeeksAgo) {
          return { name: "New", variant: "light" };
        }
      }

      const badgeTags = tags?.filter((tag) => tag?.startsWith("badge"));
      const { defaultCountryCode } = currentLocale;

      if (!badgeTags?.length) {
        return;
      }

      return badgeTags.reduce((prev: Badge | undefined, badgeTag) => {
        const badgeParts = badgeTag.split(":");
        let [, colour, name, countryCode, saleType] = badgeParts || [];

        if (prev) {
          return prev;
        }

        if (!name || !colour) {
          return prev;
        }

        // If the "country code" part looks like a sale type, then it has been omitted.
        if (!!countryCode && ["vip", "sale"].includes(countryCode)) {
          saleType = countryCode;
          countryCode = undefined;
        }

        if (
          !!countryCode &&
          defaultCountryCode?.localeCompare(countryCode) !== 0
        ) {
          return prev;
        }

        if (
          (isOnSale && saleType !== sale?.type) ||
          (!isOnSale && !!saleType)
        ) {
          return prev;
        }

        return {
          name,
          colour,
          variant: "light" === colour ? "light" : "secondary",
          saleType,
          countryCode,
        };
      }, undefined);
    },
    [currentLocale, sale, isOnSale],
  );

  return {
    decodeVariantId,
    getBadgeFromTags,
    imageSrcSets,
    imageUrl,
  };
};
